<template>
  <span>
    <el-button :type="buttonType" :disabled="disabled" @click="openUpload">
      <slot>{{ buttonText }}</slot>
    </el-button>

    <!-- Upload dialog -->
    <el-dialog
      v-model="uploadVisible"
      :title="dialogTitle"
      width="560px"
      append-to-body
      @closed="resetUpload"
    >
      <div class="excel-import">
        <div class="excel-import__hint">
          Ustunlar (1-qator — sarlavha): <b>{{ columns.join(", ") }}</b>
        </div>
        <div v-if="hint" class="excel-import__note">{{ hint }}</div>
        <el-link
          v-if="columns.length"
          type="primary"
          :underline="false"
          class="excel-import__template"
          @click="downloadTemplate"
        >
          Shablonni yuklab olish
        </el-link>
        <div v-if="columns.length" class="excel-import__note">
          Shablon CSV formatida — to'ldirgach faylni Excel'da
          "Excel Workbook (.xlsx)" formatida saqlang, aks holda yuklab bo'lmaydi.
        </div>

        <el-upload
          ref="uploadRef"
          class="excel-import__upload"
          :action="action"
          :headers="headers"
          :multiple="false"
          accept=".xlsx, .xls"
          :limit="1"
          :auto-upload="false"
          :on-exceed="onExceed"
          :on-success="onSuccess"
          :on-error="onError"
          :on-change="onFileChange"
          :on-remove="onFileRemove"
          drag
        >
          <el-icon class="el-icon--upload"><upload-filled /></el-icon>
          <div class="el-upload__text">
            Faylni yuklang yoki <em>bu yerga bosing</em>
          </div>
          <template #tip>
            <div class="el-upload__tip">Faqat .xlsx yoki .xls fayllar</div>
          </template>
        </el-upload>
      </div>
      <template #footer>
        <el-button @click="uploadVisible = false">Bekor qilish</el-button>
        <el-button
          type="primary"
          :loading="uploading"
          :disabled="!hasFile"
          @click="submit"
        >
          Yuklash
        </el-button>
      </template>
    </el-dialog>

    <!-- Result dialog -->
    <el-dialog
      v-model="resultVisible"
      title="Import natijasi"
      width="640px"
      append-to-body
    >
      <div v-if="result" class="excel-result">
        <div class="excel-result__summary">
          <div>Jami qatorlar: <b>{{ result.total ?? 0 }}</b></div>
          <div v-for="field in summaryFields" :key="field.key">
            {{ field.label }}: <b>{{ result[field.key] ?? 0 }}</b>
          </div>
          <div
            v-if="failedRows.length"
            class="excel-result__failed-count"
          >
            Xato qatorlar: <b>{{ failedRows.length }}</b>
          </div>
        </div>

        <el-table
          v-if="failedRows.length"
          :data="failedRows"
          border
          size="small"
          max-height="320"
          style="margin-top: 12px"
        >
          <el-table-column prop="row" label="Qator" width="90" />
          <el-table-column prop="error" label="Xato" />
        </el-table>
      </div>
      <template #footer>
        <el-button type="primary" @click="resultVisible = false">Yopish</el-button>
      </template>
    </el-dialog>
  </span>
</template>

<script>
import { ElMessage } from "element-plus";
import { UploadFilled } from "@element-plus/icons-vue";

export default {
  name: "MfExcelImport",
  components: { UploadFilled },
  props: {
    // Full upload URL (incl. base + any query, e.g. ?board_id=53).
    action: { type: String, required: true },
    dialogTitle: { type: String, default: "Excel orqali import" },
    buttonText: { type: String, default: "Excel import" },
    buttonType: { type: String, default: "primary" },
    disabled: { type: Boolean, default: false },
    // Header labels — shown as a hint and used for the CSV template.
    columns: { type: Array, default: () => [] },
    // Extra numeric counters from the response, e.g.
    // [{ key: "assigned", label: "Biriktirildi" }].
    summaryFields: { type: Array, default: () => [] },
    templateName: { type: String, default: "shablon.csv" },
    // Optional extra note shown under the columns hint (e.g. format warnings).
    hint: { type: String, default: "" },
  },
  emits: ["completed"],
  data() {
    return {
      uploadVisible: false,
      resultVisible: false,
      uploading: false,
      hasFile: false,
      result: null,
    };
  },
  computed: {
    headers() {
      return { Authorization: `${this.$store.state.accessToken}` };
    },
    failedRows() {
      return this.result?.failed || [];
    },
  },
  methods: {
    openUpload() {
      this.uploadVisible = true;
    },
    resetUpload() {
      this.hasFile = false;
      this.uploading = false;
      this.$refs.uploadRef?.clearFiles();
    },
    onFileChange(file) {
      this.hasFile = !!file;
    },
    onFileRemove() {
      this.hasFile = false;
    },
    onExceed() {
      ElMessage.warning("Faqat bitta fayl yuklash mumkin!");
    },
    submit() {
      if (!this.hasFile) return;
      this.uploading = true;
      this.$refs.uploadRef.submit();
    },
    onSuccess(response) {
      this.uploading = false;
      this.uploadVisible = false;
      this.result = response || {};
      this.resultVisible = true;
      this.$emit("completed", this.result);
    },
    onError(error) {
      this.uploading = false;
      let message = "Import vaqtida xatolik sodir bo'ldi";
      try {
        message = JSON.parse(error?.message)?.message || message;
      } catch (e) {
        // keep default message
      }
      ElMessage.error(message);
    },
    downloadTemplate() {
      // No client xlsx library — provide a CSV template (opens in Excel).
      const csv = "﻿" + this.columns.join(",") + "\n";
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", this.templateName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
  },
};
</script>

<style scoped>
.excel-import__hint {
  margin-bottom: 8px;
  color: #606266;
  line-height: 1.5;
}

.excel-import__note {
  margin-bottom: 12px;
  color: #e6a23c;
  font-size: 13px;
  line-height: 1.5;
}

.excel-import__template {
  margin-bottom: 12px;
}

.excel-result__summary {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 14px;
}

.excel-result__failed-count {
  color: #f56c6c;
}
</style>
